import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import { isWhite, getNumericValue, getRootFontSize } from '../../utils/stringUtils.js';
import { alpha } from '@mui/material';
import { getDefaultThemePalette, DEFAULT_THEME_OPTIONS } from '../defaultTheme.js';
import { toNumber } from 'lodash';
import { filterShallowUndefinedObjectEntries } from '../../utils/objectUtils.js';
import { DTCComponentName, ButtonName } from '../componentNames.js';

const CHECKOUT_COMPONENT_LEGACY_THEME_KEYS = [
    { legacyKey: 'membershipButton', contentKey: 'membershipButton', variantKey: 'signedOutDesktop' },
    { legacyKey: 'membershipButton', contentKey: 'membershipButton', variantKey: 'signedInDesktop' },
    { legacyKey: 'membershipButtonMobile', contentKey: 'membershipButton', variantKey: 'signedOutMobile' },
    { legacyKey: 'membershipButtonMobile', contentKey: 'membershipButton', variantKey: 'signedInMobile' },
    { legacyKey: 'bagButton', contentKey: 'cartButton', variantKey: 'signedOutDesktop' },
    { legacyKey: 'bagButton', contentKey: 'cartButton', variantKey: 'signedInDesktop' },
    { legacyKey: 'bagButtonMobile', contentKey: 'cartButton', variantKey: 'signedOutMobile' },
    { legacyKey: 'bagButtonMobile', contentKey: 'cartButton', variantKey: 'signedInMobile' },
    { legacyKey: 'profileButton', contentKey: 'profileButton', variantKey: 'signedOutDesktop' },
    { legacyKey: 'profileButton', contentKey: 'profileButton', variantKey: 'signedInDesktop' },
    { legacyKey: 'profileButtonMobile', contentKey: 'profileButton', variantKey: 'signedOutMobile' },
    { legacyKey: 'profileButtonMobile', contentKey: 'profileButton', variantKey: 'signedInMobile' },
    { legacyKey: 'buyButton', contentKey: 'addToBagButton', variantKey: 'signedOutDesktop' },
    { legacyKey: 'buyButton', contentKey: 'addToBagButton', variantKey: 'signedInDesktop' },
    { legacyKey: 'buyButtonMobile', contentKey: 'addToBagButton', variantKey: 'signedOutMobile' },
    { legacyKey: 'buyButtonMobile', contentKey: 'addToBagButton', variantKey: 'signedInMobile' },
];
const getObjectIfHasValues = (obj) => {
    if (!obj) {
        return undefined;
    }
    return Object.values(obj).every((value) => [undefined, null, ''].includes(value)) ? undefined : obj;
};
const findLegacyThemeValue = (legacyThemeValue, key, condition) => {
    var _a;
    if (!legacyThemeValue) {
        return undefined;
    }
    // for legacy theme, there may be multiple components that specify the same key with conflicting values. so we return the one with the most occurrences
    const valueMap = new Map();
    Object.values(legacyThemeValue).forEach((component) => {
        if (typeof component !== 'object' || !component) {
            return;
        }
        const value = component[key];
        if (value) {
            valueMap.set(value, (valueMap.get(value) || 0) + 1);
        }
    });
    const sortedValues = Array.from(valueMap.entries()).sort((a, b) => b[1] - a[1]);
    return (_a = sortedValues.find(([value]) => (condition ? condition(value) : true))) === null || _a === void 0 ? void 0 : _a[0];
};
const normalizeFontWeight = (value) => {
    if (typeof value === 'string') {
        // if the value is something like "700" we return 700
        const numericValue = toNumber(value);
        if (Number.isFinite(numericValue)) {
            return numericValue;
        }
        // if the value is something like "Bold" we return "bold"
        return value.toLowerCase();
    }
    return value;
};
const getThemeColors = (legacyTheme) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    // buy button is usually primary color, so we use its font color as the contrast text color for all colors
    // custom theme will override and select a good contrast text color if needed
    const legacyContrastText = (_b = (_a = legacyTheme.inpageCheckoutComponent) === null || _a === void 0 ? void 0 : _a.buyButton) === null || _b === void 0 ? void 0 : _b.fontColor;
    const defaultPalette = getDefaultThemePalette();
    let accent = undefined;
    const background = {};
    let primary = {
        contrastText: legacyContrastText,
        main: defaultPalette.primary.main,
    };
    const secondary = {
        contrastText: legacyContrastText,
        main: defaultPalette.secondary.main,
    };
    const tertiary = {
        contrastText: (_c = legacyTheme.primary) !== null && _c !== void 0 ? _c : legacyContrastText,
        main: defaultPalette.tertiary.main,
    };
    let textColor = undefined;
    const legacyAnimationDotColor = findLegacyThemeValue(legacyTheme.inpageCheckoutComponent, 'animationDotColor');
    if (legacyAnimationDotColor) {
        accent = { main: legacyAnimationDotColor, contrastText: legacyContrastText };
    }
    if ((_e = (_d = legacyTheme.inpageCheckoutComponent) === null || _d === void 0 ? void 0 : _d.buyButton) === null || _e === void 0 ? void 0 : _e.backgroundColor) {
        primary = {
            main: legacyTheme.inpageCheckoutComponent.buyButton.backgroundColor,
            contrastText: legacyTheme.inpageCheckoutComponent.buyButton.fontColor,
        };
    }
    if (legacyTheme.componentStyleOverrides) {
        const legacySecondaryColor = findLegacyThemeValue(legacyTheme.componentStyleOverrides, 'secondary');
        if (legacySecondaryColor) {
            secondary.main = legacySecondaryColor;
        }
    }
    if (legacyTheme.secondary) {
        // allow to override the secondary color obtained from the componentStyleOverrides
        secondary.main = legacyTheme.secondary;
    }
    if (legacyTheme.primary) {
        // previously the primary color was used as the text color, there was no distinction between primary and text color
        textColor = legacyTheme.primary;
    }
    if (legacyTheme.tertiary &&
        ![primary === null || primary === void 0 ? void 0 : primary.main, secondary === null || secondary === void 0 ? void 0 : secondary.main, '', '#'].includes((_f = legacyTheme.tertiary) === null || _f === void 0 ? void 0 : _f.trim()) &&
        !isWhite(legacyTheme.tertiary) // unlikely that white was intentionally set as the tertiary color. legacy theme has default values which are unused at times.
    ) {
        tertiary.main = legacyTheme.tertiary;
    }
    if ((_g = legacyTheme.dtcGetApp) === null || _g === void 0 ? void 0 : _g.backgroundColor) {
        background.alternate = legacyTheme.dtcGetApp.backgroundColor;
    }
    if ((_k = (_j = (_h = legacyTheme.componentStyleOverrides) === null || _h === void 0 ? void 0 : _h.dialog) === null || _j === void 0 ? void 0 : _j.backgroundColor) === null || _k === void 0 ? void 0 : _k.trim()) {
        background.paper = legacyTheme.componentStyleOverrides.dialog.backgroundColor;
        background.default = background.paper;
    }
    return {
        accent: getObjectIfHasValues(accent),
        background: getObjectIfHasValues(background),
        primary: getObjectIfHasValues(primary),
        secondary: getObjectIfHasValues(secondary),
        tertiary: getObjectIfHasValues(tertiary),
        default: {
            main: textColor,
        },
        textColor,
    };
};
const getThemeSmartBannerType = (legacyTheme) => {
    var _a, _b, _c;
    const androidSmartBannerEnabled = !!((_a = legacyTheme.smartAppBanner) === null || _a === void 0 ? void 0 : _a.androidPopup);
    const iosSmartBannerEnabled = !!((_b = legacyTheme.smartAppBanner) === null || _b === void 0 ? void 0 : _b.iosSmartBanner) || !!((_c = legacyTheme.getAppBanner) === null || _c === void 0 ? void 0 : _c.iosSmartBanner);
    if (androidSmartBannerEnabled && iosSmartBannerEnabled) {
        return 'both';
    }
    if (androidSmartBannerEnabled) {
        return 'android';
    }
    if (iosSmartBannerEnabled) {
        return 'ios';
    }
    return 'none';
};
const getThemeAppSettings = (legacyTheme) => {
    var _a, _b, _c, _d, _e;
    const themeAppSettings = {
        smartBannerType: getThemeSmartBannerType(legacyTheme),
    };
    if (legacyTheme.dtcGetApp) {
        themeAppSettings.dtcGetAppImage = ((_a = legacyTheme.dtcGetApp) === null || _a === void 0 ? void 0 : _a.appImage) || undefined;
    }
    if (legacyTheme.getAppBanner) {
        themeAppSettings.iosCustomBannerAlignment = {
            align: (_b = legacyTheme.getAppBanner.iosCustomBanner) === null || _b === void 0 ? void 0 : _b.align,
            alignOffset: (_c = legacyTheme.getAppBanner.iosCustomBanner) === null || _c === void 0 ? void 0 : _c.alignOffset,
        };
        themeAppSettings.androidCustomBannerAlignment = {
            align: (_d = legacyTheme.getAppBanner.androidCustomBanner) === null || _d === void 0 ? void 0 : _d.align,
            alignOffset: (_e = legacyTheme.getAppBanner.androidCustomBanner) === null || _e === void 0 ? void 0 : _e.alignOffset,
        };
    }
    return themeAppSettings;
};
const getThemeFeatureFlags = (legacyTheme) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const enableLocationMap = (_c = (_b = (_a = legacyTheme.inpageCheckoutComponent) === null || _a === void 0 ? void 0 : _a.buyButton) === null || _b === void 0 ? void 0 : _b.enableLocationMap) !== null && _c !== void 0 ? _c : (_e = (_d = legacyTheme.inpageCheckoutComponent) === null || _d === void 0 ? void 0 : _d.buyButtonMobile) === null || _e === void 0 ? void 0 : _e.enableLocationMap;
    const enableScrollSliderDisplay = ((_g = (_f = legacyTheme.inpageCheckoutComponent) === null || _f === void 0 ? void 0 : _f.buyButton) === null || _g === void 0 ? void 0 : _g.enableScrollSliderDisplay) ||
        ((_j = (_h = legacyTheme.inpageCheckoutComponent) === null || _h === void 0 ? void 0 : _h.buyButtonMobile) === null || _j === void 0 ? void 0 : _j.enableScrollSliderDisplay);
    const enableCartCountNumber = (_m = (_l = (_k = legacyTheme.inpageCheckoutComponent) === null || _k === void 0 ? void 0 : _k.bagButton) === null || _l === void 0 ? void 0 : _l.countNumber) !== null && _m !== void 0 ? _m : (_p = (_o = legacyTheme.inpageCheckoutComponent) === null || _o === void 0 ? void 0 : _o.bagButtonMobile) === null || _p === void 0 ? void 0 : _p.countNumber;
    return {
        bagAnimationDot: true,
        scrollSliderDisplay: enableScrollSliderDisplay,
        storeLocatorMap: enableLocationMap,
        cartCountNumber: enableCartCountNumber,
    };
};
const getThemePillPresets = (legacyTheme) => {
    const globalComponentPillPresets = {};
    const legacyPillStyles = legacyTheme.customRewardPillColor;
    if (legacyPillStyles) {
        globalComponentPillPresets.default = getObjectIfHasValues({
            main: legacyPillStyles.default,
            contrastText: legacyPillStyles.defaultText,
        });
        globalComponentPillPresets.outOfStock = getObjectIfHasValues({
            main: legacyPillStyles.outOfStock,
            contrastText: legacyPillStyles.outOfStockText,
        });
        globalComponentPillPresets.purchase = getObjectIfHasValues({
            main: legacyPillStyles.purchase,
            contrastText: legacyPillStyles.purchaseText,
        });
        globalComponentPillPresets.survey = getObjectIfHasValues({
            main: legacyPillStyles.survey,
            contrastText: legacyPillStyles.surveyText,
        });
        globalComponentPillPresets.content = getObjectIfHasValues({
            main: legacyPillStyles.content,
            contrastText: legacyPillStyles.contentText,
        });
        globalComponentPillPresets.affiliate = getObjectIfHasValues({
            main: legacyPillStyles.affiliate,
            contrastText: legacyPillStyles.affiliateText,
        });
        globalComponentPillPresets.dailyDeal = getObjectIfHasValues({
            main: legacyPillStyles.dailyDeal,
            contrastText: legacyPillStyles.dailyDealText,
        });
    }
    return getObjectIfHasValues(globalComponentPillPresets);
};
const getThemeComponentPresets = (legacyTheme) => {
    var _a, _b, _c, _d, _e, _f;
    const iconSize = findLegacyThemeValue({
        membershipButton: (_a = legacyTheme.inpageCheckoutComponent) === null || _a === void 0 ? void 0 : _a.membershipButton,
        bagButton: (_b = legacyTheme.inpageCheckoutComponent) === null || _b === void 0 ? void 0 : _b.bagButton,
        profileButton: (_c = legacyTheme.inpageCheckoutComponent) === null || _c === void 0 ? void 0 : _c.profileButton,
        buyButton: (_d = legacyTheme.inpageCheckoutComponent) === null || _d === void 0 ? void 0 : _d.buyButton,
    }, 'size');
    const borderWidth = getNumericValue((_f = (_e = legacyTheme.inpageCheckoutComponent) === null || _e === void 0 ? void 0 : _e.buyButton) === null || _f === void 0 ? void 0 : _f.borderWidth);
    const componentOptions = {
        BCSolidButton: {
            styleOverrides: {
                root: {
                    borderRadius: legacyTheme.globalButtonRadius,
                },
            },
        },
        BCOutlineButton: {
            styleOverrides: {
                root: Object.assign({ borderRadius: legacyTheme.globalButtonRadius }, (borderWidth && { borderWidth })),
            },
        },
        BCTextButton: {
            styleOverrides: {
                root: {
                    borderRadius: legacyTheme.globalButtonRadius,
                },
            },
        },
        BCIconButton: {
            defaultProps: {
                height: getNumericValue(iconSize),
            },
            styleOverrides: {
                root: {
                    borderRadius: legacyTheme.globalButtonRadius,
                },
            },
        },
    };
    return componentOptions;
};
const getThemeContentPresets = (legacyTheme, isShopDomain) => {
    var _a;
    const themeContent = {};
    if ((_a = legacyTheme.customConnectReward) === null || _a === void 0 ? void 0 : _a.headerText) {
        themeContent.connectRewardHeader = {
            signedOutDesktop: legacyTheme.customConnectReward.headerText,
            signedInDesktop: legacyTheme.customConnectReward.headerText,
            signedOutMobile: legacyTheme.customConnectReward.headerText,
            signedInMobile: legacyTheme.customConnectReward.headerText,
        };
    }
    if (legacyTheme.inpageCheckoutComponent) {
        // extract content from legacy inpage checkout components
        CHECKOUT_COMPONENT_LEGACY_THEME_KEYS.forEach(({ legacyKey, contentKey, variantKey }) => {
            var _a;
            const legacyComponent = (_a = legacyTheme.inpageCheckoutComponent) === null || _a === void 0 ? void 0 : _a[legacyKey];
            if (legacyComponent) {
                const isTextButton = 'textButton' in legacyComponent ? legacyComponent.textButton : !!legacyComponent.text;
                const dtcImgUrl = 'dtcImageUrl' in legacyComponent ? legacyComponent.dtcImageUrl : undefined;
                const imgUrl = 'imageUrl' in legacyComponent ? legacyComponent.imageUrl : undefined;
                const customIconStartSrc = (isShopDomain && dtcImgUrl) || imgUrl;
                themeContent[contentKey] = Object.assign(Object.assign({}, themeContent[contentKey]), { [variantKey]: {
                        text: isTextButton ? legacyComponent.text : undefined,
                        customIconStartSrc: !isTextButton ? customIconStartSrc : undefined,
                    } });
            }
        });
    }
    return themeContent;
};
const resolveLegacyFont = (legacyTheme, key) => {
    var _a, _b, _c, _d, _e, _f;
    // for checkout bar themes, we should prioritize the barFonts, for all other themes we should prioritize the dtcFonts and only use the other as a fallback
    return legacyTheme.activeInpageCheckout
        ? (_b = (_a = legacyTheme.dtcFonts) === null || _a === void 0 ? void 0 : _a[key]) !== null && _b !== void 0 ? _b : (_c = legacyTheme.barFonts) === null || _c === void 0 ? void 0 : _c[key]
        : (_e = (_d = legacyTheme.barFonts) === null || _d === void 0 ? void 0 : _d[key]) !== null && _e !== void 0 ? _e : (_f = legacyTheme.dtcFonts) === null || _f === void 0 ? void 0 : _f[key];
};
const getThemeTypography = (legacyTheme) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    // THEME FONT SETTINGS
    const themeFontSettings = {};
    themeFontSettings.numericFontOffsetY = toNumber(findLegacyThemeValue(legacyTheme.inpageCheckoutComponent, 'countNumberYOffset'));
    if (isNaN(themeFontSettings.numericFontOffsetY)) {
        themeFontSettings.numericFontOffsetY = undefined;
    }
    const fontWeight = normalizeFontWeight((_d = (_b = (_a = legacyTheme.font) === null || _a === void 0 ? void 0 : _a.fontWeight) !== null && _b !== void 0 ? _b : (_c = legacyTheme.dtcFonts) === null || _c === void 0 ? void 0 : _c.fontWeight) !== null && _d !== void 0 ? _d : (_e = legacyTheme.barFonts) === null || _e === void 0 ? void 0 : _e.fontWeight);
    const fontFamilyLink = resolveLegacyFont(legacyTheme, 'fontFamilyLink');
    const assignedFonts = resolveLegacyFont(legacyTheme, 'assignedFonts');
    const viewBagTextOffsetY = resolveLegacyFont(legacyTheme, 'viewBagTextOffsetY');
    themeFontSettings.fontFamilyLink = fontFamilyLink;
    themeFontSettings.assignedFonts = assignedFonts;
    themeFontSettings.fontWeight = fontWeight;
    themeFontSettings.fontOffsetY = viewBagTextOffsetY;
    // THEME TYPOGRAPHY
    const baseFontSize = resolveLegacyFont(legacyTheme, 'baseFontSize');
    const fontFamily = resolveLegacyFont(legacyTheme, 'fontFamily');
    const btnFontWeight = normalizeFontWeight((_g = (_f = legacyTheme.inpageCheckoutComponent) === null || _f === void 0 ? void 0 : _f.buyButton) === null || _g === void 0 ? void 0 : _g.fontWeight);
    const lgButtonFontSize = getNumericValue((_j = (_h = legacyTheme.inpageCheckoutComponent) === null || _h === void 0 ? void 0 : _h.buyButton) === null || _j === void 0 ? void 0 : _j.fontSize);
    const DEFAULT_BROWSER_FONT_SIZE = 16;
    const htmlFontSize = (_k = getRootFontSize()) !== null && _k !== void 0 ? _k : DEFAULT_BROWSER_FONT_SIZE;
    const typography = {
        fontFamily,
        fontSize: baseFontSize,
        htmlFontSize,
        xsButton: Object.assign(Object.assign({}, DEFAULT_THEME_OPTIONS.typography.xsButton), { fontWeight: btnFontWeight, fontFamily: fontFamily !== null && fontFamily !== void 0 ? fontFamily : 'inherit' }),
        smButton: Object.assign(Object.assign({}, DEFAULT_THEME_OPTIONS.typography.smButton), { fontWeight: btnFontWeight, fontFamily: fontFamily !== null && fontFamily !== void 0 ? fontFamily : 'inherit' }),
        button: Object.assign(Object.assign({}, DEFAULT_THEME_OPTIONS.typography.button), { fontWeight: btnFontWeight, fontFamily: fontFamily !== null && fontFamily !== void 0 ? fontFamily : 'inherit' }),
        lgButton: Object.assign(Object.assign(Object.assign({}, DEFAULT_THEME_OPTIONS.typography.lgButton), (!!lgButtonFontSize && { fontSize: `${lgButtonFontSize}px` })), { fontWeight: btnFontWeight, fontFamily: fontFamily !== null && fontFamily !== void 0 ? fontFamily : 'inherit' }),
    };
    return { fontSettings: themeFontSettings, typography };
};
const getThemeComponentCSSOverrides = (legacyTheme) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4;
    const buyButtonMobileTheme = (_a = legacyTheme.inpageCheckoutComponent) === null || _a === void 0 ? void 0 : _a.buyButtonMobile;
    const buyButtonTheme = (_b = legacyTheme.inpageCheckoutComponent) === null || _b === void 0 ? void 0 : _b.buyButton;
    const bagButton = (_c = legacyTheme.inpageCheckoutComponent) === null || _c === void 0 ? void 0 : _c.bagButton;
    const bagButtonMobile = (_d = legacyTheme.inpageCheckoutComponent) === null || _d === void 0 ? void 0 : _d.bagButtonMobile;
    // the checkout bar refers to the bag button as cart button
    const checkoutBarBagButton = (_e = legacyTheme.componentStyleOverrides) === null || _e === void 0 ? void 0 : _e.cartButton;
    const membershipButton = (_f = legacyTheme.inpageCheckoutComponent) === null || _f === void 0 ? void 0 : _f.membershipButton;
    const membershipButtonMobile = (_g = legacyTheme.inpageCheckoutComponent) === null || _g === void 0 ? void 0 : _g.membershipButtonMobile;
    const profileButton = (_h = legacyTheme.inpageCheckoutComponent) === null || _h === void 0 ? void 0 : _h.profileButton;
    const profileButtonMobile = (_j = legacyTheme.inpageCheckoutComponent) === null || _j === void 0 ? void 0 : _j.profileButtonMobile;
    const locationMapSearchResultsButton = (_k = legacyTheme.componentStyleOverrides) === null || _k === void 0 ? void 0 : _k.locationMapSearchResultsButton;
    const checkoutQuantityControl = (_l = legacyTheme.componentStyleOverrides) === null || _l === void 0 ? void 0 : _l.checkoutQuantityControl;
    const emptyBagMessageCard = (_m = legacyTheme.componentStyleOverrides) === null || _m === void 0 ? void 0 : _m.emptyBagMessageCard;
    const emptyBagMessageCardButton = (_o = legacyTheme.componentStyleOverrides) === null || _o === void 0 ? void 0 : _o.emptyBagMessageCardButton;
    const outlineStyle = legacyTheme.activeInpageCheckout === false && !!checkoutBarBagButton
        ? {
            outlineColor: checkoutBarBagButton.tertiary,
        }
        : undefined;
    // Helper to check if all values in an object are empty
    const isEmptyObject = (obj) => {
        return Object.values(obj).every((value) => value === undefined || value === null || value === '');
    };
    // Helper to add entry only if it has non-empty values
    const addIfNotEmpty = (target, key, breakpoints) => {
        const validBreakpoints = {};
        if (breakpoints.xs && !isEmptyObject(breakpoints.xs)) {
            validBreakpoints.xs = breakpoints.xs;
        }
        if (breakpoints.md && !isEmptyObject(breakpoints.md)) {
            validBreakpoints.md = breakpoints.md;
        }
        if (Object.keys(validBreakpoints).length > 0) {
            target[key] = validBreakpoints;
        }
    };
    const componentCSSOverrides = {};
    addIfNotEmpty(componentCSSOverrides, DTCComponentName.EmptyBagMessageCard, {
        xs: {
            backgroundColor: (emptyBagMessageCard === null || emptyBagMessageCard === void 0 ? void 0 : emptyBagMessageCard.backgroundColor) || undefined,
            color: (emptyBagMessageCard === null || emptyBagMessageCard === void 0 ? void 0 : emptyBagMessageCard.textColor) || undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.AddToBagButton, {
        xs: {
            color: (_p = buyButtonMobileTheme === null || buyButtonMobileTheme === void 0 ? void 0 : buyButtonMobileTheme.fontColor) !== null && _p !== void 0 ? _p : undefined,
            height: (_q = buyButtonMobileTheme === null || buyButtonMobileTheme === void 0 ? void 0 : buyButtonMobileTheme.buttonHeight) !== null && _q !== void 0 ? _q : undefined,
            width: (buyButtonMobileTheme === null || buyButtonMobileTheme === void 0 ? void 0 : buyButtonMobileTheme.fullWidth) ? '100%' : undefined,
            borderRadius: (_r = buyButtonMobileTheme === null || buyButtonMobileTheme === void 0 ? void 0 : buyButtonMobileTheme.borderRadius) !== null && _r !== void 0 ? _r : undefined,
        },
        md: {
            color: (_s = buyButtonTheme === null || buyButtonTheme === void 0 ? void 0 : buyButtonTheme.fontColor) !== null && _s !== void 0 ? _s : undefined,
            height: (_t = buyButtonTheme === null || buyButtonTheme === void 0 ? void 0 : buyButtonTheme.buttonHeight) !== null && _t !== void 0 ? _t : undefined,
            width: (buyButtonTheme === null || buyButtonTheme === void 0 ? void 0 : buyButtonTheme.fullWidth) ? '100%' : undefined,
            borderRadius: (_u = buyButtonTheme === null || buyButtonTheme === void 0 ? void 0 : buyButtonTheme.borderRadius) !== null && _u !== void 0 ? _u : undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.BagDrawerCheckoutButton, {
        xs: {
            borderRadius: (_x = (_w = (_v = legacyTheme.componentStyleOverrides) === null || _v === void 0 ? void 0 : _v.bagDrawerCheckoutButton) === null || _w === void 0 ? void 0 : _w.globalButtonRadius) !== null && _x !== void 0 ? _x : undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.BagDrawerViewBagButton, {
        xs: {
            borderRadius: (_0 = (_z = (_y = legacyTheme.componentStyleOverrides) === null || _y === void 0 ? void 0 : _y.bagDrawerViewBagButton) === null || _z === void 0 ? void 0 : _z.globalButtonRadius) !== null && _0 !== void 0 ? _0 : undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.BagButton, {
        xs: Object.assign(Object.assign(Object.assign({}, outlineStyle), { color: (_1 = bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.textFontColor) !== null && _1 !== void 0 ? _1 : undefined }), ((bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.textButton)
            ? {
                // text button
                fontSize: getNumericValue(bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.size),
                width: getNumericValue(bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.size),
                fontSize: (_2 = getNumericValue(bagButtonMobile === null || bagButtonMobile === void 0 ? void 0 : bagButtonMobile.countNumberSize)) !== null && _2 !== void 0 ? _2 : undefined,
            })),
        md: Object.assign({ color: (_3 = bagButton === null || bagButton === void 0 ? void 0 : bagButton.textFontColor) !== null && _3 !== void 0 ? _3 : undefined }, ((bagButton === null || bagButton === void 0 ? void 0 : bagButton.textButton)
            ? {
                // text button
                fontSize: getNumericValue(bagButton === null || bagButton === void 0 ? void 0 : bagButton.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(bagButton === null || bagButton === void 0 ? void 0 : bagButton.size),
                width: getNumericValue(bagButton === null || bagButton === void 0 ? void 0 : bagButton.size),
                fontSize: (_4 = getNumericValue(bagButton === null || bagButton === void 0 ? void 0 : bagButton.countNumberSize)) !== null && _4 !== void 0 ? _4 : undefined,
            })),
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.EmptyBagMessageCardButton, {
        xs: {
            color: (emptyBagMessageCardButton === null || emptyBagMessageCardButton === void 0 ? void 0 : emptyBagMessageCardButton.textColor) || undefined,
            backgroundColor: (emptyBagMessageCardButton === null || emptyBagMessageCardButton === void 0 ? void 0 : emptyBagMessageCardButton.backgroundColor) || undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.CheckoutQuantityControl, {
        xs: {
            backgroundColor: (checkoutQuantityControl === null || checkoutQuantityControl === void 0 ? void 0 : checkoutQuantityControl.backgroundColor) || undefined,
            color: (checkoutQuantityControl === null || checkoutQuantityControl === void 0 ? void 0 : checkoutQuantityControl.textColor) || undefined,
        },
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.MembershipButton, {
        xs: Object.assign({ color: (membershipButtonMobile === null || membershipButtonMobile === void 0 ? void 0 : membershipButtonMobile.textFontColor) || undefined }, ((membershipButtonMobile === null || membershipButtonMobile === void 0 ? void 0 : membershipButtonMobile.textButton)
            ? {
                // text button
                fontSize: getNumericValue(membershipButtonMobile === null || membershipButtonMobile === void 0 ? void 0 : membershipButtonMobile.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(membershipButtonMobile === null || membershipButtonMobile === void 0 ? void 0 : membershipButtonMobile.size),
                width: getNumericValue(membershipButtonMobile === null || membershipButtonMobile === void 0 ? void 0 : membershipButtonMobile.size),
            })),
        md: Object.assign({ color: (membershipButton === null || membershipButton === void 0 ? void 0 : membershipButton.textFontColor) || undefined }, ((membershipButton === null || membershipButton === void 0 ? void 0 : membershipButton.textButton)
            ? {
                // text button
                fontSize: getNumericValue(membershipButton === null || membershipButton === void 0 ? void 0 : membershipButton.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(membershipButton === null || membershipButton === void 0 ? void 0 : membershipButton.size),
                width: getNumericValue(membershipButton === null || membershipButton === void 0 ? void 0 : membershipButton.size),
            })),
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.ProfileButton, {
        xs: Object.assign({ color: (profileButtonMobile === null || profileButtonMobile === void 0 ? void 0 : profileButtonMobile.textFontColor) || undefined }, ((profileButtonMobile === null || profileButtonMobile === void 0 ? void 0 : profileButtonMobile.textButton)
            ? {
                // text button
                fontSize: getNumericValue(profileButtonMobile === null || profileButtonMobile === void 0 ? void 0 : profileButtonMobile.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(profileButtonMobile === null || profileButtonMobile === void 0 ? void 0 : profileButtonMobile.size),
                width: getNumericValue(profileButtonMobile === null || profileButtonMobile === void 0 ? void 0 : profileButtonMobile.size),
            })),
        md: Object.assign({ color: (profileButton === null || profileButton === void 0 ? void 0 : profileButton.textFontColor) || undefined }, ((profileButton === null || profileButton === void 0 ? void 0 : profileButton.textButton)
            ? {
                // text button
                fontSize: getNumericValue(profileButton === null || profileButton === void 0 ? void 0 : profileButton.textFontSize),
            }
            : {
                // icon button
                height: getNumericValue(profileButton === null || profileButton === void 0 ? void 0 : profileButton.size),
                width: getNumericValue(profileButton === null || profileButton === void 0 ? void 0 : profileButton.size),
            })),
    });
    addIfNotEmpty(componentCSSOverrides, ButtonName.LocationMapSearchResultsButton, {
        xs: {
            backgroundColor: (locationMapSearchResultsButton === null || locationMapSearchResultsButton === void 0 ? void 0 : locationMapSearchResultsButton.backgroundColor) || undefined,
            color: (locationMapSearchResultsButton === null || locationMapSearchResultsButton === void 0 ? void 0 : locationMapSearchResultsButton.textColor) || undefined,
        },
    });
    return componentCSSOverrides;
};
const isLegacyTheme = (legacyTheme) => {
    if (typeof legacyTheme !== 'object' || !legacyTheme) {
        return false;
    }
    return 'activeInpageCheckout' in legacyTheme || 'componentStyleOverrides' in legacyTheme;
};
const isServerTheme = (theme) => {
    if (typeof theme !== 'object' || !theme) {
        return false;
    }
    return 'settings' in theme && 'content' in theme;
};
const parseLegacyDtcThemeResponse = (legacyTheme, isShopDomain = false) => {
    if (!isLegacyTheme(legacyTheme) && isServerTheme(legacyTheme)) {
        // we have to check both to ensure that we don't return an object that has both legacy and server theme properties
        return legacyTheme;
    }
    if (!isLegacyTheme(legacyTheme)) {
        // we don't want to handle a non-legacy theme
        return undefined;
    }
    const dtcMode = legacyTheme.activeInpageCheckout ? 'inpage' : 'checkout-bar';
    const { fontSettings, typography } = getThemeTypography(legacyTheme);
    const theme = Object.assign({ settings: {
            app: getThemeAppSettings(legacyTheme),
            featureFlags: getThemeFeatureFlags(legacyTheme),
            fonts: fontSettings,
        }, dtcMode, content: getThemeContentPresets(legacyTheme, isShopDomain), globalComponentPresets: {
            pill: getThemePillPresets(legacyTheme),
        }, components: getThemeComponentPresets(legacyTheme), componentCSSOverrides: getThemeComponentCSSOverrides(legacyTheme), typography }, getThemeColors(legacyTheme));
    return theme;
};
const buildThemeFromServerTheme = (styleData, isShopDomain = false) => {
    if (!styleData) {
        return undefined;
    }
    const parsedTheme = parseLegacyDtcThemeResponse(styleData, isShopDomain);
    if (parsedTheme && typeof parsedTheme === 'object' && 'primary' in parsedTheme) {
        const { primary, secondary, tertiary, accent, background, draftId: _draftId, textColor } = parsedTheme, rest = __rest(parsedTheme, ["primary", "secondary", "tertiary", "accent", "background", "draftId", "textColor"]);
        const palette = Object.assign({ primary,
            secondary,
            tertiary,
            accent,
            background, text: {
                primary: textColor,
                secondary: textColor && alpha(textColor, 0.7),
            } }, (textColor && {
            default: {
                main: textColor,
            },
        }));
        return filterShallowUndefinedObjectEntries(Object.assign(Object.assign({}, rest), { palette }));
    }
    // theme already parsed
    return styleData;
};

export { buildThemeFromServerTheme, parseLegacyDtcThemeResponse };
//# sourceMappingURL=parseLegacyDtcThemeResponse.js.map
